// src/utils/capabilityError.ts
//
// Turning a capability refusal into something a person can act on.
//
// The server answers a denied mutation with a precise, machine-readable body:
//
//   { error: 'Insufficient permissions',
//     message: 'Missing capability: crm:contacts:delete',
//     code: 'CAPABILITY_DENIED' }
//
// Surfaces that dropped it into `console.error` left the user clicking a
// button that did nothing at all — no dialog change, no message, no clue that
// permission was the reason. Silence is the worst possible answer to "why
// didn't that work", because it is indistinguishable from a broken app.
//
// WHY THIS IS SEPARATE FROM HIDING THE CONTROL
//   Hiding a control the user cannot use is the better everyday experience,
//   and it is done from the capability hooks. It is NOT a substitute for
//   this. The client is never the authority: its capability map can be stale
//   by seconds (an admin edits access mid-session), the control may predate
//   the change, and a determined caller can hit the API directly. So the
//   refusal has to read well when it does arrive.
//
// WHY THE SERVER'S `message` IS NOT SHOWN VERBATIM
//   "Missing capability: crm:contacts:delete" is exactly right for a log and
//   useless to the person reading it. The scope name is not vocabulary the
//   product ever taught them. We say what they cannot do and who can change
//   it, and keep the raw code available for support.

/** RTK Query surfaces server bodies under `data`. */
interface MaybeRtkError {
  status?: unknown;
  data?: {
    code?: unknown;
    error?: unknown;
    message?: unknown;
  } | null;
}

function bodyOf(error: unknown): { code?: unknown; message?: unknown } | null {
  if (!error || typeof error !== 'object') return null;
  const data = (error as MaybeRtkError).data;
  if (!data || typeof data !== 'object') return null;
  return data;
}

/**
 * Did the server refuse this because of a missing capability?
 *
 * Keyed on the `CAPABILITY_DENIED` code rather than the 403 status: a 403 can
 * mean several things (record scope, workspace mismatch, a legacy role gate),
 * and promising the user "ask an admin for a capability" when the real reason
 * was something else would send them somewhere that cannot help.
 */
export function isCapabilityDenied(error: unknown): boolean {
  return bodyOf(error)?.code === 'CAPABILITY_DENIED';
}

/** The raw `Missing capability: …` string, for support and diagnostics. */
export function capabilityDeniedDetail(error: unknown): string | null {
  const message = bodyOf(error)?.message;
  return typeof message === 'string' && message ? message : null;
}

/**
 * A sentence to show the user.
 *
 * `action` is described in product words — "delete this contact", not a scope
 * — and is what makes the message specific enough to be worth reading.
 * Returns null when this was not a capability refusal, so callers can fall
 * through to their own error handling rather than mislabelling a network
 * failure as a permissions problem.
 */
export function capabilityDeniedMessage(
  error: unknown,
  action: string,
): string | null {
  if (!isCapabilityDenied(error)) return null;
  return `You do not have permission to ${action}. Ask a workspace admin to grant it.`;
}
